import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { errorSummaryTag } from '@ui/elementTags';
import { applyNhsGlobal } from '@ui/styles/applyNhsGlobal';
import errorSummaryStyles from '@ui/styles/nhs-subsets/nhs-error-summary.scss?inline';

export interface ErrorSummaryItem {
  message: string;
  href?: string;
}

export interface ErrorSummaryLinkClickDetail {
  targetId: string;
}

export const errorSummaryLinkClickEvent = 'inav-error-summary-link-click';

@customElement(errorSummaryTag)
export class ErrorSummaryComponent extends LitElement {
  @property({ type: String })
  public title = 'There is a problem';

  @property({ type: String })
  public message = '';

  @property({ attribute: false })
  public errors: readonly ErrorSummaryItem[] = [];

  protected createRenderRoot(): HTMLElement | ShadowRoot {
    return applyNhsGlobal(this, errorSummaryStyles);
  }

  protected updated(changedProperties: PropertyValues<this>): void {
    const previousHasContent = this.getHasContent(
      changedProperties.get('message') as string | undefined,
      changedProperties.get('errors') as readonly ErrorSummaryItem[] | undefined,
    );

    if (this.hasContent && !previousHasContent) {
      queueMicrotask(() => {
        this.focusSummary();
      });
    }
  }

  // Lets hosts re-announce an already-visible summary, e.g. on a repeat failed submission.
  public focusSummary(): void {
    this.renderRoot.querySelector<HTMLElement>('.nhsuk-error-summary')?.focus();
  }

  public render() {
    if (!this.hasContent) {
      return nothing;
    }

    const items = this.errors.length > 0 ? this.errors : [{ message: this.message }];

    return html`
      <div
        class="nhsuk-error-summary"
        data-module="nhsuk-error-summary"
        role="alert"
        aria-labelledby="error-summary-title"
        tabindex="-1"
      >
        <h2 class="nhsuk-error-summary__title" id="error-summary-title">${this.title}</h2>
        <div class="nhsuk-error-summary__body">
          <ul class="nhsuk-list nhsuk-error-summary__list">
            ${items.map((item) => html`
              <li>
                ${item.href
                  ? html`<a href=${item.href} @click=${this.handleLinkClick}>${item.message}</a>`
                  : item.message}
              </li>
            `)}
          </ul>
        </div>
      </div>
    `;
  }

  private get hasContent(): boolean {
    return this.getHasContent(this.message, this.errors);
  }

  private getHasContent(message?: string, errors?: readonly ErrorSummaryItem[]): boolean {
    return Boolean(message) || (errors?.length ?? 0) > 0;
  }

  private readonly handleLinkClick = (event: Event): void => {
    const anchor = event.currentTarget as HTMLAnchorElement;
    const href = anchor.getAttribute('href');

    if (!href?.startsWith('#') || href.length < 2) {
      return;
    }

    event.preventDefault();
    const nextUrl = new URL(globalThis.location.href);
    nextUrl.hash = href;
    globalThis.history.replaceState(globalThis.history.state, '', nextUrl);

    this.dispatchEvent(new CustomEvent<ErrorSummaryLinkClickDetail>(errorSummaryLinkClickEvent, {
      bubbles: true,
      composed: true,
      detail: {
        targetId: href.slice(1),
      },
    }));
  };
}

declare global {
  interface HTMLElementTagNameMap {
    [errorSummaryTag]: ErrorSummaryComponent;
  }
}